import CloseIcon from '@mui/icons-material/Close';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import EngineeringIcon from '@mui/icons-material/Engineering';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import React from 'react';
import { pageSurface } from './alliance-surface';
import { BANNER_TONES } from './alliance-tokens';
import { useIsMobileLayout } from './bar-controls';
import { useConnectionTrouble } from './connection-monitor';
import { CURRENT_APP } from './current-app';

/**
 * The banner slot under the App Bar: one queue any code can push to.
 *
 * - Priority: error > warning > info; ties newest first.
 * - At most two strips show; the rest collapse into "+N more" on the second
 *   strip, which expands the list.
 * - Dismissals are kept in memory only (approved Oct 1 2026), so a dismissed
 *   banner comes back after a refresh or in a new tab.
 */

type BannerTone = 'info' | 'warning' | 'error';

interface BannerAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  outlined?: boolean;
}

export interface AppBanner {
  /** Stable id; pushing the same id again replaces the banner */
  id: string;
  tone: BannerTone;
  message: React.ReactNode;
  icon?: React.ReactNode;
  /** Small uppercase tag after the message ("Admins only", "Retrying") */
  tag?: string;
  actions?: BannerAction[];
  /** Show a close button; closing hides it until the next page load */
  dismissible?: boolean;
  /** Change this to re-push a banner whose message content changed */
  revision?: string;
}

interface QueuedBanner extends AppBanner {
  createdAt: number;
}

const TONE_RANK: Record<BannerTone, number> = { error: 0, warning: 1, info: 2 };

const BannersContext = React.createContext<{
  banners: QueuedBanner[];
  push: (banner: AppBanner) => void;
  remove: (id: string) => void;
  dismiss: (id: string) => void;
}>({
  banners: [],
  push: () => undefined,
  remove: () => undefined,
  dismiss: () => undefined,
});

/** Holds the banner queue. Wrap the app (above the router) in this */
export function AppBannersProvider(props: { children: React.ReactNode }) {
  // State
  const [banners, setBanners] = React.useState<QueuedBanner[]>([]);
  // Ref
  const dismissed = React.useRef(new Set<string>());

  const push = React.useCallback((banner: AppBanner) => {
    if (dismissed.current.has(banner.id)) {
      return;
    }
    setBanners((current) => {
      const existing = current.find((item) => item.id === banner.id);
      const next = current.filter((item) => item.id !== banner.id);
      return [
        ...next,
        { ...banner, createdAt: existing?.createdAt ?? Date.now() },
      ];
    });
  }, []);

  const remove = React.useCallback((id: string) => {
    setBanners((current) => current.filter((item) => item.id !== id));
  }, []);

  const dismiss = React.useCallback(
    (id: string) => {
      dismissed.current.add(id);
      remove(id);
    },
    [remove],
  );

  const value = React.useMemo(
    () => ({ banners, push, remove, dismiss }),
    [banners, push, remove, dismiss],
  );

  return (
    <BannersContext.Provider value={value}>
      {props.children}
    </BannersContext.Provider>
  );
}

/** push / remove / dismiss banners from anywhere under AppBannersProvider */
function useAppBanners() {
  const { push, remove, dismiss } = React.useContext(BannersContext);
  return { push, remove, dismiss };
}

/**
 * Keep a banner in the queue while this component is mounted (or while
 * `banner` is non-null).
 */
function useBanner(banner: AppBanner | null) {
  // Hooks
  const { push, remove } = useAppBanners();
  // Ref
  const latest = React.useRef(banner);
  latest.current = banner;

  const id = banner?.id;
  // Re-push when the visible content changes (e.g. the maintenance end)
  const signature = banner
    ? JSON.stringify([
        banner.tone,
        banner.tag,
        banner.revision,
        typeof banner.message === 'string' ? banner.message : '',
      ])
    : '';

  React.useEffect(() => {
    if (!id || !latest.current) {
      return;
    }
    push(latest.current);
    return () => remove(id);
  }, [id, signature, push, remove]);
}

/** Renders the queue: full-width strips in the flow under the App Bar */
export function AppBanners() {
  // Context
  const { banners, dismiss } = React.useContext(BannersContext);
  // State
  const [expanded, setExpanded] = React.useState(false);
  // Hooks
  const isMobile = useIsMobileLayout();

  const sorted = [...banners].sort(
    (a, b) =>
      TONE_RANK[a.tone] - TONE_RANK[b.tone] || b.createdAt - a.createdAt,
  );
  const visible = expanded ? sorted : sorted.slice(0, 2);
  const hidden = sorted.length - visible.length;

  React.useEffect(() => {
    if (sorted.length <= 2 && expanded) {
      setExpanded(false);
    }
  }, [sorted.length, expanded]);

  if (!sorted.length) {
    return null;
  }

  const dark = pageSurface({ inShell: true }).dark;
  const tones = BANNER_TONES[dark ? 'dark' : 'light'];

  return (
    <Box sx={{ letterSpacing: 'normal' }}>
      {visible.map((banner, index) => {
        const tone = tones[banner.tone];
        // Tag and hover fills: the tone's base color at low alpha
        const base = tone.border.slice(0, 7);
        const tagBg = `${base}${dark ? '26' : '1f'}`;
        const hover = `${base}${dark ? '1f' : '1a'}`;
        const isLast = index === visible.length - 1;

        return (
          <Box
            key={banner.id}
            role={banner.tone === 'error' ? 'alert' : 'status'}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '8px 12px',
              p: isMobile ? '10px 12px 10px 14px' : '8px 16px 8px 20px',
              backgroundColor: tone.bg,
              borderBottom: `1px solid ${tone.border}`,
              color: tone.text,
              fontSize: 14,
              lineHeight: 1.4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                color: tone.icon,
                '& .MuiSvgIcon-root': { fontSize: 20 },
              }}
            >
              {banner.icon}
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 200,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '4px 10px',
                '& strong': { fontWeight: 700 },
              }}
            >
              <span>{banner.message}</span>
              {banner.tag ? (
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    p: '3px 8px',
                    borderRadius: '6px',
                    backgroundColor: tagBg,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {banner.tag}
                </Box>
              ) : null}
              {isLast && hidden > 0 ? (
                <ButtonBase
                  onClick={() => setExpanded(true)}
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  +{hidden} more
                </ButtonBase>
              ) : null}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                ml: 'auto',
              }}
            >
              {(banner.actions || []).map((action) => (
                <ButtonBase
                  key={action.label}
                  onClick={action.onClick}
                  sx={{
                    fontFamily: 'inherit',
                    height: 32,
                    px: '12px',
                    borderRadius: '8px',
                    gap: '6px',
                    fontSize: 13,
                    fontWeight: 500,
                    color: tone.text,
                    border: `1px solid ${
                      action.outlined ? tone.border : 'transparent'
                    }`,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                    '&:hover': { backgroundColor: hover },
                  }}
                >
                  {action.icon}
                  {action.label}
                </ButtonBase>
              ))}
              {banner.dismissible ? (
                <ButtonBase
                  aria-label="Dismiss"
                  title="Dismiss"
                  onClick={() => dismiss(banner.id)}
                  sx={{
                    fontFamily: 'inherit',
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    color: tone.text,
                    '&:hover': { backgroundColor: hover },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </ButtonBase>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * "Sat, Oct 4 · 6:00 AM EDT" (or "Saturday, …" with long). Returns null for
 * an empty or invalid value.
 */
export const formatAllianceDate = (params: {
  value: string;
  long?: boolean;
}) => {
  if (!params.value) {
    return null;
  }
  const date = new Date(params.value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const day = date.toLocaleDateString('en-US', {
    weekday: params.long ? 'long' : 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `${day} · ${time}`;
};

/**
 * Admins-only maintenance strip with Dismiss and Sign out. Agents get the
 * full-page MaintenanceScreen instead.
 */
export function MaintenanceBanner(props: {
  until: string;
  onSignOut: () => void;
}) {
  // Hooks
  const { dismiss } = useAppBanners();

  const until = formatAllianceDate({ value: props.until });
  const id = 'maintenance';

  useBanner({
    id,
    tone: 'warning',
    icon: <EngineeringIcon />,
    message: until ? (
      <React.Fragment>
        Maintenance is being performed until <strong>{until}</strong>
      </React.Fragment>
    ) : (
      'Maintenance is being performed'
    ),
    tag: 'Admins only',
    revision: props.until,
    actions: [
      { label: 'Dismiss', onClick: () => dismiss(id) },
      {
        label: 'Sign out',
        icon: <LogoutIcon />,
        outlined: true,
        onClick: props.onSignOut,
      },
    ],
  });

  return null;
}

/** Connection trouble (error, not dismissible, clears itself) */
export function ConnectionBanner() {
  const troubled = useConnectionTrouble();

  useBanner(
    troubled
      ? {
          id: 'connection',
          tone: 'error',
          icon: <CloudOffIcon />,
          message:
            'We’re having trouble connecting. Some information may be out of date.',
          tag: 'Retrying',
        }
      : null,
  );

  return null;
}

// The hashed main bundle in a CRA/Vite index.html (CRA 4 names it
// main.<hash>.chunk.js, CRA 5 main.<hash>.js)
const MAIN_SCRIPT =
  /\/static\/js\/main\.[0-9a-f]+(?:\.chunk)?\.js|\/assets\/index-[\w-]+\.js/;
const CHECK_INTERVAL_MS = 60000;

/**
 * Shows "{App} has been updated" when a newer build is deployed. On window
 * focus and on route change (at most once a minute) it re-fetches
 * index.html and compares the main bundle's hashed name with the one this
 * page loaded. Does nothing in development, where the bundle isn't hashed.
 *
 * @param pathname - The current route, so navigation triggers a check
 */
export function UpdatedBanner(props: { pathname: string }) {
  // State
  const [updated, setUpdated] = React.useState(false);
  // Ref
  const lastCheck = React.useRef(0);

  const check = React.useCallback(async () => {
    const current = Array.from(document.scripts)
      .map((script) => script.src.match(MAIN_SCRIPT)?.[0])
      .find(Boolean);
    if (
      !current ||
      updated ||
      Date.now() - lastCheck.current < CHECK_INTERVAL_MS
    ) {
      return;
    }
    lastCheck.current = Date.now();
    try {
      const response = await fetch(
        `${process.env.PUBLIC_URL || ''}/index.html`,
        {
          cache: 'no-store',
        },
      );
      const latest = (await response.text()).match(MAIN_SCRIPT)?.[0];
      if (latest && latest !== current) {
        setUpdated(true);
      }
    } catch (error) {
      // Offline or blocked: try again on the next focus or navigation
    }
  }, [updated]);

  React.useEffect(() => {
    check();
  }, [props.pathname, check]);

  React.useEffect(() => {
    window.addEventListener('focus', check);
    return () => window.removeEventListener('focus', check);
  }, [check]);

  useBanner(
    updated
      ? {
          id: 'updated',
          tone: 'info',
          icon: <SystemUpdateAltIcon />,
          message: (
            <React.Fragment>
              <strong>{CURRENT_APP.name} has been updated. </strong>Reload to
              get the latest version.
            </React.Fragment>
          ),
          actions: [
            {
              label: 'Reload',
              icon: <RefreshIcon />,
              outlined: true,
              onClick: () => window.location.reload(),
            },
          ],
          dismissible: true,
        }
      : null,
  );

  return null;
}
